/* eslint-disable react-refresh/only-export-components */
import {
  useActivateExternalUser,
  useGetExternalUsers,
  useInactivateExternalUser,
  useResetExternalUserPassword,
} from "@/queries/users/external-users.api";
import type { ExternalUserType } from "@/types/entities/external-users";
import { Permissions } from "@/utils/permissions";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  TableData,
  useToast,
} from "@elaniintc/goes-design-system";
import TablePaginantion from "@/components/common/table/pagination";
import {
  createColumnHelper,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { FiltersModal } from "@/components/ui/filters/FiltersModal";
import { filterTypesEnum, type FilterOption } from "@/types/common/filters";
import { TableLeadingCell } from "@/components/ui/table-leading-cell";
import type { AxiosError } from "axios";
import DeactivateModal from "../details/edit/deactivate-modal";
import ReactivateModal from "../details/edit/reactivate-modal";
import ResetPasswordModal from "../details/edit/reset-password-modal";
import { useAuthorization } from "@/hooks/use-authorization";

const columnHelper = createColumnHelper<ExternalUserType>();

const getDocumentTypeLabel = (documentType: string): string => {
  const normalizedType = documentType.toUpperCase();

  const labels: Record<string, string> = {
    DUI: "DUI",
    NIT: "NIT",
    PASSPORT: "Pasaporte",
    CARNE_INSTITUCIONAL: "Carné",
  };
  return labels[normalizedType] || documentType;
};

const columnsDef = ({
  onCellButtonClick,
  onDeactivate,
  onActivate,
  onResetPassword,
}: {
  onCellButtonClick: (id: string) => void;
  onDeactivate: (id: string) => void;
  onActivate: (id: string) => void;
  onResetPassword: (id: string) => void;
}) => {
  return [
    columnHelper.accessor("first_name", {
      id: "first_name",
      header: "Nombre",
      meta: {
        cellClassName: "group",
      },
      cell: (info) => {
        const { first_name, last_name, id } = info.row.original;
        const fullName = last_name ? `${first_name} ${last_name}` : first_name;
        const maxLen = 60;
        const displayName =
          fullName && fullName.length > maxLen
            ? fullName.slice(0, maxLen - 3) + "..."
            : fullName;
        return (
          <TableLeadingCell
            title={displayName || "-"}
            showButton
            onButtonClick={() => onCellButtonClick(id)}
          />
        );
      },
    }),
    columnHelper.accessor("type", {
      id: "type",
      header: "Tipo",
      meta: {
        className: "hidden lg:table-cell",
      },
      cell: (info) => {
        const type = info.getValue();
        const normalizedType = type.toUpperCase();

        const typeLabels: Record<string, string> = {
          APPLICANT: "Solicitante",
          FACILITATOR: "Tramitador",
          REPRESENTATIVE: "Representante",
          INSTITUTION: "Institución",
        };
        return typeLabels[normalizedType] || type || "-";
      },
    }),
    columnHelper.accessor("status", {
      enableSorting: false,
      header: "Estado",
      meta: {
        className: "hidden sm:table-cell",
      },
      cell: (info) => {
        const status = info.getValue();
        if (status === 1) {
          return (
            <Badge
              variant="success"
              shape="pill"
              className="h-6 text-sm leading-2xl"
            >
              Activo
            </Badge>
          );
        }
        if (status === 0) {
          return (
            <Badge
              variant="neutral"
              shape="pill"
              className="h-6 text-sm leading-2xl"
            >
              Inactivo
            </Badge>
          );
        }
        return "-";
      },
    }),
    columnHelper.display({
      id: "identity",
      header: "Identidad",
      meta: {
        className: "hidden mdPlus:table-cell",
      },
      cell: (info) => {
        const { document_number, document_type } = info.row.original;
        const maxLen = 40;
        const displayDoc =
          document_number && document_number.length > maxLen
            ? document_number.slice(0, maxLen - 3) + "..."
            : document_number;
        return (
          <div className="flex items-center justify-between w-full">
            <span className="text-sm font-medium">{displayDoc || "-"}</span>
            {document_type && (
              <Badge
                variant="neutral"
                size="sm"
                shape="square"
                className="font-normal"
              >
                {getDocumentTypeLabel(document_type)}
              </Badge>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor("updated_at", {
      id: "updated_at",
      header: "Última Actualización",
      meta: {
        className: "hidden md:table-cell",
      },
      cell: (info) => {
        const date = info.getValue();
        return date ? dayjs(date).format("MMM DD, YYYY | HH:mm A") : "-";
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      meta: {
        className: "w-[56px]",
      },
      cell: (info) => {
        const userId = info.row.original.id;
        return (
          <div className="w-full flex justify-end">
            <Menu
              id={userId}
              isUserInactive={info.row.original.status === 0}
              onActivate={() => onActivate(userId)}
              onDeactivate={() => onDeactivate(userId)}
              onResetPassword={() => onResetPassword(userId)}
            />
          </div>
        );
      },
    }),
  ];
};

const Menu = ({
  id,
  isUserInactive,
  onActivate,
  onDeactivate,
  onResetPassword,
}: {
  id: string;
  isUserInactive: boolean;
  onDeactivate: () => void;
  onActivate: () => void;
  onResetPassword: () => void;
}) => {
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.users.update);
  const canUpdate = hasPermission(Permissions.users.update);
  const canResetPassword = hasPermission(Permissions.users.update);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="size-8">
          <Icon
            type="far"
            name="ellipsis-vertical"
            className="text-current text-sm"
          />
          <span className="sr-only">Acciones para usuario externo</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral-silent border text-body font-normal p-2"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {canEdit && !isUserInactive && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              asChild
            >
              <NavLink to={`/usuarios/externos/${id}`}>Editar usuario</NavLink>
            </DropdownMenuItem>
          )}
          {canUpdate && isUserInactive && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={onActivate}
            >
              Reactivar usuario
            </DropdownMenuItem>
          )}
          {canUpdate && !isUserInactive && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={onDeactivate}
            >
              Desactivar usuario
            </DropdownMenuItem>
          )}
          {canResetPassword && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={onResetPassword}
            >
              Restablecer contraseña
            </DropdownMenuItem>
          )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

let openExternalFiltersModal: (() => void) | null = null;

export const triggerExternalFiltersModal = () => {
  if (openExternalFiltersModal) {
    openExternalFiltersModal();
  }
};

const ExternalUsersList = ({ searchQuery }: { searchQuery?: string }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuthorization();
  const canEdit = hasPermission(Permissions.users.update);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [page, setPage] = useState<number>(1);

  const [modalState, setModalState] = useState<
    | "idle"
    | "deactivate-warning"
    | "reactivate-warning"
    | "reset-password-warning"
    | "filters"
  >("idle");

  const [created_at, setCreatedAt] = useQueryState(
    "created_at_external",
    parseAsString,
  );
  const [status, setStatus] = useQueryState("status_external", parseAsString);
  const [order_by, setOrderBy] = useQueryState(
    "order_by_external",
    parseAsString,
  );
  const [user_type, setUserType] = useQueryState(
    "user_type_external",
    parseAsString,
  );

  useEffect(() => {
    openExternalFiltersModal = () => setModalState("filters");
    return () => {
      openExternalFiltersModal = null;
    };
  }, []);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const [formatFilters, setFormatFilters] = useState<FilterOption[]>([
    {
      id: 1,
      field: "created_at",
      type: filterTypesEnum.dates,
      label: "Fecha de creación",
      isUpdated: !!created_at,
      shouldDisabledFutureDates: true,
      result: created_at || "",
    },
    {
      id: 2,
      field: "status",
      type: filterTypesEnum.status,
      label: "Estado del usuario",
      isUpdated: !!status,
      result: status || "",
      status: [
        {
          field: "Activo",
          label: "Activo",
          value: status ? status.includes("Activo") : false,
        },
        {
          field: "Inactivo",
          label: "Inactivo",
          value: status ? status.includes("Inactivo") : false,
        },
      ],
    },
    {
      id: 3,
      field: "type",
      type: filterTypesEnum.status,
      label: "Tipo de usuario",
      isUpdated: !!user_type,
      result: user_type || "",
      status: [
        {
          field: "applicant",
          label: "Solicitante",
          value: user_type ? user_type.includes("applicant") : false,
        },
        {
          field: "facilitator",
          label: "Tramitador",
          value: user_type ? user_type.includes("facilitator") : false,
        },
        {
          field: "representative",
          label: "Representante",
          value: user_type ? user_type.includes("representative") : false,
        },
        {
          field: "institution",
          label: "Institución",
          value: user_type ? user_type.includes("institution") : false,
        },
      ],
    },
  ]);

  useEffect(() => {
    setFormatFilters((prevFilters) =>
      prevFilters.map((filter) => {
        if (filter.field === "created_at") {
          return {
            ...filter,
            isUpdated: !!created_at,
            result: created_at || "",
          };
        }
        if (
          filter.field === "status" &&
          filter.type === filterTypesEnum.status
        ) {
          return {
            ...filter,
            isUpdated: !!status,
            result: status || "",
            status: filter.status?.map((s) => ({
              ...s,
              value: status ? status.includes(s.field) : false,
            })),
          };
        }
        if (filter.field === "type" && filter.type === filterTypesEnum.status) {
          return {
            ...filter,
            isUpdated: !!user_type,
            result: user_type || "",
            status: filter.status?.map((s) => ({
              ...s,
              value: user_type ? user_type.includes(s.field) : false,
            })),
          };
        }
        return filter;
      }),
    );
  }, [created_at, status, user_type]);

  const getStatusValue = (statusString: string | null) => {
    if (!statusString) return undefined;
    if (statusString === "Activo") return "1";
    if (statusString === "Inactivo") return "0";
    return undefined;
  };

  const getUserTypeValue = (typeString: string | null) => {
    if (!typeString) return undefined;
    return typeString.split(",").map((t) => t.trim().toUpperCase());
  };

  const getSearchFilter = (searchQuery: string | null) => {
    if (!searchQuery) return {};

    const hasDigits = /\d/.test(searchQuery);

    if (hasDigits) {
      return { search: searchQuery };
    } else {
      return { name: searchQuery };
    }
  };

  const { data } = useGetExternalUsers({
    page,
    per_page: 20,
    ...(status ? { status: getStatusValue(status) } : {}),
    ...(order_by ? { group_by: order_by } : {}),
    ...getSearchFilter(searchQuery || null),
    ...(created_at ? { created_at } : {}),
    ...(user_type ? { user_type: getUserTypeValue(user_type) } : {}),
  });

  const shouldShowSkeleton = !data;

  const [selectedUserToAction, setSelectedUserToAction] = useState<
    string | null
  >(null);

  const {
    mutateAsync: mutateToInactiveUser,
    isPending: isLoadingInactivateUser,
  } = useInactivateExternalUser(selectedUserToAction!);
  const { mutateAsync: mutateToActiveUser, isPending: isLoadingActivateUser } =
    useActivateExternalUser(selectedUserToAction!);
  const {
    mutateAsync: mutateResetPassword,
    isPending: isLoadingResetPassword,
  } = useResetExternalUserPassword(selectedUserToAction!);

  const columns = useMemo(() => {
    const c = columnsDef({
      onDeactivate: (id: string) => {
        setSelectedUserToAction(id);
        setModalState("deactivate-warning");
      },
      onActivate: (id: string) => {
        setSelectedUserToAction(id);
        setModalState("reactivate-warning");
      },
      onResetPassword: (id: string) => {
        setSelectedUserToAction(id);
        setModalState("reset-password-warning");
      },
      onCellButtonClick: (id: string) => {
        navigate(`/usuarios/externos/${id}`);
      },
    });
    return c.filter(({ id }) => {
      if (id === "actions") return canEdit;
      return true;
    });
  }, [canEdit, navigate]);

  const onDeactivate = async (description: string) => {
    try {
      await mutateToInactiveUser({ description });
      showToast({
        title: "Usuario desactivado",
        description: "El usuario externo ha sido desactivado correctamente.",
        options: {
          type: "success",
          duration: 4000,
        },
      });
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
      }>;
      showToast({
        title: "Error al desactivar usuario",
        description:
          error.response?.data?.message ||
          "Ha ocurrido un error al intentar desactivar el usuario.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    } finally {
      setSelectedUserToAction(null);
      setModalState("idle");
    }
  };

  const onActivate = async (description: string) => {
    try {
      await mutateToActiveUser({ description });
      showToast({
        title: "Usuario activado",
        description: "El usuario externo ha sido activado correctamente.",
        options: {
          type: "success",
          duration: 4000,
        },
      });
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
      }>;
      showToast({
        title: "Error al activar usuario",
        description:
          error.response?.data?.message ||
          "Ha ocurrido un error al intentar activar el usuario.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    } finally {
      setSelectedUserToAction(null);
      setModalState("idle");
    }
  };

  const onResetPassword = async () => {
    try {
      await mutateResetPassword();
      showToast({
        title: "Contraseña restablecida",
        description:
          "Se ha enviado un correo electrónico con las instrucciones para restablecer la contraseña.",
        options: {
          type: "success",
          duration: 4000,
        },
      });
    } catch (e) {
      const error = e as AxiosError<{
        message: string;
      }>;
      showToast({
        title: "Error al restablecer contraseña",
        description:
          error.response?.data?.message ||
          "Ha ocurrido un error al intentar restablecer la contraseña.",
        options: {
          type: "error",
          duration: 4000,
        },
      });
    } finally {
      setSelectedUserToAction(null);
      setModalState("idle");
    }
  };

  const onApplyFilters = (newFilters: FilterOption[]): FilterOption[] => {
    newFilters.forEach((f) => {
      if (f.isUpdated) {
        if (f.field === "created_at") {
          setCreatedAt(f.result || null);
        }
        if (f.field === "status") {
          setStatus(f.result || null);
        }
        if (f.field === "type") {
          setUserType(f.result || null);
        }
        setPage(1);
      }
    });

    const updatedFilters = newFilters.map((f) => {
      if (f.result === "") {
        return {
          ...f,
          isUpdated: false,
          result: "",
        };
      }
      return f;
    });
    return updatedFilters;
  };

  const onCloseFilters = (type: string) => {
    if (type !== "clean") {
      setModalState("idle");
      return;
    }
    setFormatFilters([
      {
        id: 1,
        field: "created_at",
        type: filterTypesEnum.dates,
        label: "Fecha de creación",
        shouldDisabledFutureDates: true,
        isUpdated: false,
        result: "",
      },
      {
        id: 2,
        field: "status",
        type: filterTypesEnum.status,
        label: "Estado del usuario",
        isUpdated: false,
        result: "",
        status: [
          { field: "Activo", label: "Activo", value: false },
          { field: "Inactivo", label: "Inactivo", value: false },
        ],
      },
      {
        id: 3,
        field: "type",
        type: filterTypesEnum.status,
        label: "Tipo de usuario",
        isUpdated: false,
        result: "",
        status: [
          { field: "applicant", label: "Solicitante", value: false },
          { field: "facilitator", label: "Tramitador", value: false },
          { field: "representative", label: "Representante", value: false },
          { field: "institution", label: "Institución", value: false },
        ],
      },
    ]);
    setCreatedAt(null);
    setUserType(null);
    setStatus(null);
    setModalState("idle");
  };

  return (
    <>
      {modalState === "filters" && (
        <FiltersModal
          isOpen={modalState === "filters"}
          onToggle={onCloseFilters}
          hasCurrentFilters={formatFilters.some((filter) => filter.isUpdated)}
          items={formatFilters}
          onApply={(newFilters) => {
            setFormatFilters(onApplyFilters(newFilters));
            setModalState("idle");
          }}
        />
      )}

      {modalState === "deactivate-warning" && selectedUserToAction && (
        <DeactivateModal
          isOpen={modalState === "deactivate-warning"}
          onClose={() => {
            setSelectedUserToAction(null);
            setModalState("idle");
          }}
          userId={selectedUserToAction}
          isLoading={isLoadingInactivateUser}
          onAccept={(data: { description: string }) => {
            onDeactivate(data.description);
          }}
        />
      )}

      {modalState === "reactivate-warning" && selectedUserToAction && (
        <ReactivateModal
          isOpen={modalState === "reactivate-warning"}
          onClose={() => {
            setSelectedUserToAction(null);
            setModalState("idle");
          }}
          userId={selectedUserToAction}
          isLoading={isLoadingActivateUser}
          onAccept={(data: { description: string }) => {
            onActivate(data.description);
          }}
        />
      )}

      {modalState === "reset-password-warning" && selectedUserToAction && (
        <ResetPasswordModal
          isOpen={modalState === "reset-password-warning"}
          onClose={() => {
            setSelectedUserToAction(null);
            setModalState("idle");
          }}
          isLoading={isLoadingResetPassword}
          onAccept={() => {
            onResetPassword();
          }}
        />
      )}

      <TableData
        wrapperClassName="min-h-0 flex-1 overflow-auto h-full relative"
        headerClassName="sticky top-0 bg-neutral-object z-20"
        columns={columns as ColumnDef<ExternalUserType>[]}
        data={data?.data || []}
        sorting={sorting}
        onSortingChange={(val) => {
          setSorting(val);
          if (val.length === 0) {
            setOrderBy("!created_at");
            return;
          }
          setOrderBy(
            val.length > 0
              ? `${val[0]?.desc ? "!" : ""}${val[0].id}`
              : "!created_at",
          );
        }}
        isFirstLoading={shouldShowSkeleton}
        stripped
      />
      <div className="p-3">
        <TablePaginantion
          totalPages={data?.meta?.last_page || 0}
          totalItems={data?.meta?.total || 0}
          pageSize={data?.data.length || 0}
          currentPage={data?.meta?.current_page || 1}
          hasNext={
            (data?.meta?.current_page || 0) < (data?.meta?.last_page || 0)
          }
          hasPrevious={(data?.meta?.current_page || 0) > 1}
          onPageChange={(page) => {
            setPage(page);
          }}
          onNext={() => {
            if (data?.meta?.current_page && data?.meta?.last_page) {
              if (data.meta?.current_page < data.meta?.last_page) {
                setPage(data.meta?.current_page + 1);
              }
            }
          }}
          onPrevious={() => {
            if (data?.meta?.current_page && data?.meta?.last_page) {
              if (data.meta?.current_page > 1) {
                setPage(data.meta?.current_page - 1);
              }
            }
          }}
        />
      </div>
    </>
  );
};

export default ExternalUsersList;
